import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useActions } from '../../hooks/useActions';

import { ANON_CLIENT_IDENTIFIER } from '../../utils/config';

import AutocompleteUi from '../UI/Autocomplete/Autocomplete';

export const ClientSelect = memo((props) => {
  const {
    handleChange,
    selectedId,
    excludeIds = [],
    forceClientFetch = false,
    fullWidth = false,
    selfSx,
  } = props;
  const { t } = useTranslation();
  const { getAllClients } = useActions();
  const { clients: clientsReducer, authentication } = useSelector(
    (state) => state,
  );

  const { clients, isLoading } = clientsReducer;
  const { organizationId } = authentication?.practitioner || {};

  const [selectedClient, setSelectedClient] = useState(null);

  const clientsFilter = useMemo(() => {
    const filter = {
      where: {
        organizationId,
        name: { neq: ANON_CLIENT_IDENTIFIER },
      },
      fields: ['id', 'name'],
    };
    return `filter=${encodeURIComponent(JSON.stringify(filter))}`;
  }, [organizationId]);

  const options = useMemo(
    () =>
      clients
        .filter((client) => !excludeIds?.includes(client?.id))
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: 'base',
          }),
        )
        .map((client) => ({ text: client.name, value: client.id })),
    [clients, excludeIds],
  );

  /**
   * Handle Autocomplete`s options render
   * @param optionProps option props
   * @param obj option text and value
   * @returns ReactNode for each Autocomplete option
   */
  const handleAutocompleteRenderOptions = useCallback(
    (optionProps, { text, value }) => (
      <li {...optionProps} key={value}>
        {text}
      </li>
    ),
    [],
  );

  /**
   * Handle Autocomplete change event
   * (option selection, clear value, close...)
   * @param event
   * @param value selected option value
   */
  const handleAutocompleteChange = useCallback(
    (event, value) => {
      setSelectedClient(value);
      handleChange(value);
    },
    [handleChange],
  );

  /**
   * Fetches all clients if they are not defined
   * Sets autocomplete value to selectedId
   */
  useEffect(() => {
    if (clients.length <= 0 || forceClientFetch) getAllClients(clientsFilter);
    if (selectedId)
      setSelectedClient(options.find((item) => item.value === selectedId));
  }, [selectedId]);

  return (
    <AutocompleteUi
      selfSx={selfSx}
      className='client-select'
      label={t('create-treatment-wizard.select-client.client-dropdown.label')}
      placeholder={t(
        'create-treatment-wizard.select-client.client-dropdown.placeholder',
      )}
      autocompleteValue={selectedClient}
      onAutocompleteChangeHandler={handleAutocompleteChange}
      onAutocompleteRenderOptionsHandler={handleAutocompleteRenderOptions}
      options={options}
      loading={isLoading}
      width={fullWidth ? '100%' : undefined}
      withTransition
    />
  );
});
